// Which screen a fan lands on.
//
// When the home list would hold nothing but the "Food & merch ordering"
// button, the fan page opens the menu directly — one tap saved on the only
// thing they came to do. Kept out of TagPage so the rule is testable without
// rendering the whole fan page, the way ctaButtons.ts and polls.ts are.
//
// The six visibility gates below MIRROR the home-list IIFE in TagPage.tsx
// (renderHomeView). They are deliberately not consumed by it — the inline
// conditions there carry TypeScript narrowing the block bodies rely on — so
// keep both in sync. Record<BlockKey, boolean> turns a new block into a
// compile error on both sides.

import { BlockKey, DEFAULT_BLOCK_ORDER } from './fanPageBlocks';
import { isWithinMatchWindow } from './presence';

export interface HomeBlockInputs {
  /** event_kind is sports_match (or unset) — concerts get no predictor/analytics. */
  isSportsMatch: boolean;
  /** event.id && event.sportsmonks_fixture_id — analytics needs a provider fixture. */
  hasSportsmonksFixture: boolean;
  /** visibleCtaButtons(buttons, cta_enabled).length — the switch is already applied. */
  visibleCtaCount: number;
  /** openPollsFor(polls, poll_enabled).length — the switch is already applied. */
  openPollCount: number;
  /** effectiveSurvey?.survey_questions?.length ?? 0 */
  surveyQuestionCount: number;
  settings:
    | {
        match_analytics_enabled?: boolean | null;
        predictor_enabled?: boolean | null;
        survey_enabled?: boolean | null;
        ordering_enabled?: boolean | null;
      }
    | null
    | undefined;
}

/** Which home-list blocks the fan page would render, keyed by block. */
export function homeBlockVisibility(i: HomeBlockInputs): Record<BlockKey, boolean> {
  const s = i.settings;
  return {
    cta: i.visibleCtaCount > 0,
    match_analytics: i.isSportsMatch && i.hasSportsmonksFixture && !!s?.match_analytics_enabled,
    // Predictor and survey render greyed-out outside the match window, so a
    // locked one still counts as "another feature on the list".
    predictor: i.isSportsMatch && !!s?.predictor_enabled,
    survey: !!s?.survey_enabled && i.surveyQuestionCount > 0,
    poll: i.openPollCount > 0,
    ordering: !!s?.ordering_enabled,
  };
}

/** The blocks that would render, in canonical order. */
export function visibleHomeBlocks(i: HomeBlockInputs): BlockKey[] {
  const vis = homeBlockVisibility(i);
  return DEFAULT_BLOCK_ORDER.filter((k) => vis[k]);
}

export interface OrderingAvailability {
  /** club_settings.ordering_paused */
  paused: boolean;
  /** See orderingWindowOpen — never the isFanInMatchWindow state. */
  inMatchWindow: boolean;
  menuItemCount: number;
}

export type InitialView = 'home' | 'ordering';

/**
 * Land on the menu only when ordering is the ONLY block on the home list AND
 * it is actually usable: the ordering view has no gate of its own for
 * paused / closed / empty, so landing there in any of those states would show
 * a menu nobody can order from — worse than the one-button home list.
 */
export function resolveInitialView(
  blocks: readonly BlockKey[],
  o: OrderingAvailability,
): InitialView {
  const orderingAlone = blocks.length === 1 && blocks[0] === 'ordering';
  if (!orderingAlone) return 'home';
  if (o.paused || !o.inMatchWindow || o.menuItemCount <= 0) return 'home';
  return 'ordering';
}

/**
 * The presence effect's window rule (kickoff −3h … +4h, bypassed for the
 * club-portal preview and demo seats), computed synchronously from the event.
 * The landing must not read the isFanInMatchWindow state: in the commit where
 * the fan-page data arrives that state still holds its default (true).
 * No event → open, mirroring that default. An unparseable kickoff → closed.
 */
export function orderingWindowOpen(a: {
  kickoffIso: string | null | undefined;
  bypass: boolean;
  now?: Date;
}): boolean {
  if (a.bypass) return true;
  if (!a.kickoffIso) return true;
  const kickoff = new Date(a.kickoffIso);
  if (Number.isNaN(kickoff.getTime())) return false;
  return isWithinMatchWindow(kickoff, a.now ?? new Date());
}
